import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Typography, useTheme } from '@mui/material';

import { Avatar } from '@/components';
import type { EventResponse } from '@/types';
import {
  areAllMarketsResolved,
  findResolvedOutcome,
  formatProbability,
  normalizeMarketQuestion,
} from '@/utils';

interface SearchMarketListItemProps {
  event: EventResponse;
  onClick: (result: EventResponse) => void;
}

export const SearchMarketListItem = ({ event, onClick }: SearchMarketListItemProps) => {
  const theme = useTheme();
  const market = event.markets?.[0];
  const handleClick = () => onClick(event);
  const isResolved = areAllMarketsResolved(event);
  const resolvedStatus = findResolvedOutcome(event, market);

  if (!market) return null;

  // Outcome/Probability processing
  let outcomes: any[] = [];
  try {
    outcomes =
      typeof market.outcomes === 'string' ? JSON.parse(market.outcomes) : market.outcomes || [];
  } catch (e) {
    // Ignore parse errors
  }

  const firstOutcome = outcomes[0];
  const probability = firstOutcome?.price ? parseFloat(firstOutcome.price.toString()) : 0;
  const probabilityText = formatProbability(probability);
  const outcomeValue = normalizeMarketQuestion(market);

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.25,
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        opacity: isResolved ? 0.7 : 1,
        filter: isResolved ? 'grayscale(0.3)' : 'none',
        '&:hover': {
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
          opacity: 1,
          filter: 'none',
        },
      }}
    >
      <Avatar src={event?.image} alt={event?.title} size={36} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.4,
            fontSize: '0.95rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {event?.title}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
        <Box sx={{ textAlign: 'right' }}>
          {isResolved && resolvedStatus ? (
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1,
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
              }}
            >
              {resolvedStatus.displayName || resolvedStatus.name}
              {resolvedStatus.name?.toLowerCase() === 'no' ? (
                <CancelIcon sx={{ color: theme.palette.error.main, fontSize: '1rem' }} />
              ) : (
                <CheckCircleIcon color="success" sx={{ fontSize: '1rem' }} />
              )}
            </Typography>
          ) : (
            <>
              <Typography
                variant="body1"
                sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1, fontSize: '1.1rem' }}
              >
                {probabilityText}%
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.7rem' }}
              >
                {outcomeValue}
              </Typography>
            </>
          )}
        </Box>
        <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: '1.1rem' }} />
      </Box>
    </Box>
  );
};
